// services/manufacture.service.js
import { http, URL } from "./http.service";
import { errorHandler } from "./error.service";

/**
 * Create a new manufacturing order
 * @param {string} businessId - Business ID
 * @param {object} payload - Manufacturing order data
 * @returns {Promise} API response
 */
export const createManufacturingOrder = async (businessId, payload) => {
    try {
        let resp = await http.post(`${URL}/manufacturing/${businessId}/create`, payload, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get all manufacturing orders with optional filtering
 * @param {string} businessId - Business ID
 * @param {object} params - Query parameters (status, fromDate, toDate, page, limit)
 * @returns {Promise} API response
 */
export const getManufacturingOrders = async (businessId, params = {}) => {
    try {
        const queryString = new URLSearchParams(params).toString();
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/get-manufacturing?${queryString}`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get a single manufacturing order by ID
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @returns {Promise} API response
 */
export const getManufacturingOrder = async (businessId, manufacturingId) => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/${manufacturingId}`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Update a manufacturing order
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @param {object} data - Updated data
 * @returns {Promise} API response
 */
export const updateManufacturingOrder = async (businessId, manufacturingId, data) => {
    try {
        let resp = await http.put(
            `${URL}/manufacturing/${businessId}/update/${manufacturingId}`,
            data
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Delete a manufacturing order
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @param {boolean} forceDelete - Whether to force delete completed orders
 * @returns {Promise} API response
 */
export const deleteManufacturingOrder = async (businessId, manufacturingId, forceDelete = false) => {
    try {
        let resp = await http.delete(
            `${URL}/manufacturing/${businessId}/delete/${manufacturingId}`,
            {
                data: { forceDelete }
            }
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Start the manufacturing process
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @returns {Promise} API response
 */
export const startManufacturing = async (businessId, manufacturingId) => {
    try {
        let resp = await http.post(
            `${URL}/manufacturing/${businessId}/start/${manufacturingId}`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Put manufacturing process on hold
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @param {string} reason - Reason for putting on hold
 * @param {string} heldBy - Person who put it on hold (default: "System")
 * @returns {Promise} API response
 */
export const holdManufacturing = async (businessId, manufacturingId, reason, heldBy = "System") => {
    try {
        let resp = await http.post(
            `${URL}/manufacturing/${businessId}/hold/${manufacturingId}`,
            { reason, heldBy }
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Resume manufacturing process from hold
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @returns {Promise} API response
 */
export const resumeManufacturing = async (businessId, manufacturingId) => {
    try {
        let resp = await http.post(
            `${URL}/manufacturing/${businessId}/resume/${manufacturingId}`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Complete the manufacturing process
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @param {object} data - Completion data (actualCompletionDate, notes, forceComplete)
 * @returns {Promise} API response
 */
export const completeManufacturing = async (businessId, manufacturingId, data = {}) => {
    try {
        let resp = await http.post(
            `${URL}/manufacturing/${businessId}/complete/${manufacturingId}`,
            data
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get available raw materials for manufacturing
 * @param {string} businessId - Business ID
 * @returns {Promise} API response
 */
export const getAvailableRawMaterials = async (businessId) => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/get-available-raw-materials`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get production efficiency analytics
 * @param {string} businessId - Business ID
 * @param {string} timePeriod - Time period for analytics (month, quarter, year)
 * @returns {Promise} API response
 */
export const getProductionEfficiency = async (businessId, timePeriod = 'month') => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/efficiency?timePeriod=${timePeriod}`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get pending productions
 * @param {string} businessId - Business ID
 * @returns {Promise} API response
 */
export const getPendingProductions = async (businessId) => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/pending`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get productions by status
 * @param {string} businessId - Business ID
 * @param {string} status - Status to filter by (planned, in_progress, on_hold, completed, cancelled)
 * @returns {Promise} API response
 */
export const getProductionsByStatus = async (businessId, status) => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/status/${status}`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Perform quality check on manufacturing order
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @param {object} data - Quality check data (status, checkedBy, notes, passedQuantity, failedQuantity, failureReasons)
 * @returns {Promise} API response
 */
export const performQualityCheck = async (businessId, manufacturingId, data) => {
    try {
        let resp = await http.post(
            `${URL}/manufacturing/${businessId}/quality-check/${manufacturingId}`,
            data
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get manufacturing orders with hold status
 * @param {string} businessId - Business ID
 * @returns {Promise} API response
 */
export const getOnHoldProductions = async (businessId) => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/status/on_hold`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get manufacturing analytics and dashboard data
 * @param {string} businessId - Business ID
 * @returns {Promise} API response
 */
export const getManufacturingAnalytics = async (businessId) => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/analytics`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Export manufacturing data
 * @param {string} businessId - Business ID
 * @param {object} params - Export parameters (format, fromDate, toDate, status)
 * @returns {Promise} API response
 */
export const exportManufacturingData = async (businessId, params = {}) => {
    try {
        const queryString = new URLSearchParams(params).toString();
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/export?${queryString}`,
            {
                responseType: 'blob' // For file downloads
            }
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get manufacturing order by production order number
 * @param {string} businessId - Business ID
 * @param {string} productionOrderNumber - Production order number
 * @returns {Promise} API response
 */
export const getManufacturingByOrderNumber = async (businessId, productionOrderNumber) => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/order/${productionOrderNumber}`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Bulk update manufacturing orders
 * @param {string} businessId - Business ID
 * @param {array} orders - Array of orders to update
 * @returns {Promise} API response
 */
export const bulkUpdateManufacturingOrders = async (businessId, orders) => {
    try {
        let resp = await http.put(
            `${URL}/manufacturing/${businessId}/bulk-update`,
            { orders }
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get manufacturing timeline
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @returns {Promise} API response
 */
export const getManufacturingTimeline = async (businessId, manufacturingId) => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/${manufacturingId}/timeline`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

/**
 * Get manufacturing cost analysis
 * @param {string} businessId - Business ID
 * @param {string} manufacturingId - Manufacturing order ID
 * @returns {Promise} API response
 */
export const getManufacturingCostAnalysis = async (businessId, manufacturingId) => {
    try {
        let resp = await http.get(
            `${URL}/manufacturing/${businessId}/${manufacturingId}/cost-analysis`
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};

export const cancelManufacturing = async (businessId, manufacturingId, data = {}) => {
    try {
        let resp = await http.post(
            `${URL}/manufacturing/${businessId}/cancel/${manufacturingId}`,
            data
        );
        return resp;
    } catch (error) {
        errorHandler(error);
    }
};